import { ForwardedRef } from 'react';
import * as React from 'react';
import { Box, Select } from '@apia/theme';
import { TApiaFilter, TApiaFilterValue } from '../../types';
import ApiaDateFilter from './ApiaDateFilter';
import Input from './Input';
import NumberInput, { TNumberInputChangeEvent } from './fields/NumberInput';
import InputTriple from './InputTriple';
import DateHour from './DateHour';
import useMultipleRefs from '../../hooks/useMultipleRefs';
import { customEvents } from '../../utils/dom';

export interface IApiaFilter {
  filter: TApiaFilter;
  onBlur?: (ev: TApiaFilterValue) => void;
  onChange?: (
    ev: TApiaFilterValue,
  ) => void | boolean | string | Promise<boolean | string>;
  onPressEnter?: (ev: TApiaFilterValue) => void;
  className?: string;
  shouldShowRedBorderOnRequired?: boolean;
  filterTo?: boolean;
  onError?: (error: string, dateType?: 'to' | 'from') => unknown;
}

const ApiaFilterRef = (
  {
    filter,
    onChange,
    onBlur,
    onPressEnter,
    shouldShowRedBorderOnRequired = true,
    className,
    filterTo = false,
    onError,
  }: IApiaFilter,
  ref: React.Ref<HTMLInputElement | HTMLSelectElement>,
) => {
  const [value, setValue] = React.useState<TApiaFilterValue>(
    filter.currentValue ?? '',
  );

  const handleClick = React.useCallback((ev: React.MouseEvent) => {
    ev.stopPropagation();
  }, []);

  const [lastEmittedValue, setLastEmittedValue] =
    React.useState<TApiaFilterValue>(filter.currentValue);

  const filterCurrentValue = filter?.currentValue;
  React.useEffect(() => {
    if (filterCurrentValue === '') {
      setValue(String(filterCurrentValue));
      setLastEmittedValue(String(filterCurrentValue));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter?.deleteFiltersTimestamp]);
  React.useEffect(() => {
    setValue(String(filterCurrentValue));
    setLastEmittedValue(String(filterCurrentValue));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter?.changeFiltersTimestamp]);

  /**
   * ESTO NO SE PUEDE HACER PORQUE SE ROMPE EL DEBOUNCE
   * */
  /* React.useEffect(() => {
    if (filterCurrentValue !== '') {
      setValue(String(filterCurrentValue ?? ''));
      setLastEmittedValue(String(filterCurrentValue ?? ''));
    }
  }, [filterCurrentValue]);
  */

  const innerRef = React.useRef<HTMLElement | null>(null);
  const assignRefs = useMultipleRefs(
    ref as React.MutableRefObject<HTMLElement>,
    innerRef,
  );

  const hasFiredShowPanelEvent = React.useRef(false);
  React.useEffect(() => {
    if (hasFiredShowPanelEvent.current || !innerRef.current) return;
    hasFiredShowPanelEvent.current = true;
    if (
      filter.required ||
      (filter.currentValue !== '' && filter.currentValue !== undefined)
    ) {
      if (!filter.column || (filter.asAdditional && filter.column)) {
        innerRef.current?.dispatchEvent(
          new Event(customEvents.showPanel, { bubbles: true }),
        );
        innerRef.current?.dispatchEvent(
          new Event(customEvents.expandPanel, { bubbles: true }),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [innerRef.current]);

  if (filter) {
    const handleChange = (
      ev: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    ) => {
      if (
        (onChange && onChange(String(ev.target.value)) !== false) ||
        !onChange
      )
        setValue(String(ev.target.value));
    };
    const handleNumberChange = (ev: TNumberInputChangeEvent) => {
      if ((onChange && onChange(String(ev.value)) !== false) || !onChange) {
        setValue(String(ev.value));
        onBlur?.(ev.value);
      }
    };
    const handleBlur = (
      ev:
        | React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
        | React.KeyboardEvent,
    ) => {
      if (onBlur && lastEmittedValue !== value) {
        setLastEmittedValue(value);
        onBlur((ev.target as HTMLInputElement).value);
      }
    };

    const addedValues: TApiaFilterValue[] = [];
    const sortedOptions = filter.sortCombo
      ? [...(filter.options ?? [])].sort((a, b) => (a.label > b.label ? 1 : -1))
      : filter.options;
    const options = sortedOptions
      ?.map((option, idx) => {
        if (addedValues.includes(option.value)) return null;
        return (
          <option
            value={option.value}
            key={
              String(option.value) + (String(idx) ?? option.label + String(idx))
            }
          >
            {option.label}
          </option>
        );
      })
      .filter((current) => current !== null);
    if (
      filter.type === 'dateHour' &&
      filter.id === filter.groupedInputFirstId
    ) {
      return (
        <>
          <DateHour
            filter={filter}
            onChange={onChange}
            onBlur={onBlur}
            onPressEnter={onPressEnter}
            shouldShowRedBorderOnRequired={shouldShowRedBorderOnRequired}
            ref={assignRefs}
            className={`filter ${
              filter.error ||
              (shouldShowRedBorderOnRequired !== false &&
                filter.required &&
                !filter.asAdditional &&
                value === '')
                ? 'requiredFilter__Border'
                : ''
            }`}
          />
        </>
      );
    }
    if (
      filter.type === 'dateHour' &&
      filter.id !== filter.groupedInputFirstId
    ) {
      return null;
    }

    if (filter.type === 'triple' && filter.id === filter.groupedInputFirstId) {
      return (
        <InputTriple
          filter={filter}
          // ref={assignRefs}
          handleBlur={handleBlur}
          onPressEnter={onPressEnter}
          handleClick={handleClick}
          shouldShowRedBorderOnRequired={shouldShowRedBorderOnRequired}
        />
      );
    }
    if (filter.type === 'triple' && filter.id !== filter.groupedInputFirstId) {
      return null;
    }

    if (filter.type === 'date' || filter.type === 'D') {
      if (filterTo) {
        return (
          <ApiaDateFilter
            filter={filter}
            onChange={onChange}
            onBlur={onBlur}
            onPressEnter={onPressEnter}
            ref={assignRefs}
            onDateError={(error, addDateType) => {
              if (onError) {
                onError(error, addDateType ? 'to' : undefined);
              }
            }}
            className={`filter ${
              filter.errorTo ||
              filter.error ||
              (shouldShowRedBorderOnRequired !== false &&
                filter.required &&
                (filter.dateToRequired || filter.dateRequired) &&
                !filter.asAdditional &&
                filter.column !== '' &&
                filter.column !== undefined)
                ? 'requiredFilter__Border'
                : ''
            } ${className || ''}`}
          />
        );
      } else {
        return (
          <ApiaDateFilter
            filter={filter}
            onChange={onChange}
            onBlur={onBlur}
            onPressEnter={onPressEnter}
            ref={assignRefs}
            onDateError={(error, addDateType) => {
              if (onError) {
                onError(error, addDateType ? 'from' : undefined);
              }
            }}
            className={`filter ${
              filter.errorFrom ||
              filter.error ||
              (shouldShowRedBorderOnRequired !== false &&
                filter.required &&
                (filter.dateFromRequired || filter.dateRequired) &&
                !filter.asAdditional &&
                filter.column !== '' &&
                filter.column !== undefined)
                ? 'requiredFilter__Border'
                : ''
            } ${className || ''}`}
          />
        );
      }
    }
    if (
      ['apiaNumber', 'N', 'number'].includes(filter.type ?? '') &&
      !filter.options
    ) {
      return (
        <NumberInput
          aria-readonly={filter.readonly}
          readOnly={filter.readonly}
          value={value as string}
          showAsInteger={filter.showAsInteger}
          placeholder={filter.placeholder}
          onChange={handleNumberChange}
          onKeyDown={(ev) => {
            if (ev.key.toLowerCase() === 'enter') {
              handleBlur(ev);
              if (onPressEnter)
                onPressEnter((ev.target as HTMLInputElement).value);
            }
          }}
          onClick={handleClick}
          {...window.getVariant('input')}
          className={`filter ${
            filter.error ||
            (shouldShowRedBorderOnRequired !== false &&
              filter.required &&
              !filter.asAdditional &&
              value === '')
              ? 'requiredFilter__Border'
              : ''
          }`}
          title={filter.toolTip ?? filter.title ?? String(filter.id)}
          ref={assignRefs as ForwardedRef<HTMLInputElement>}
          aria-label={filter.toolTip ?? filter.title ?? String(filter.id)}
          name={filter.id as string}
          data-test-id={`filter-${filter.id}`}
        />
      );
    }

    return filter.options ? (
      <Box
      // className={`hola ${
      //   filter.error ||
      //   (shouldShowRedBorderOnRequired !== false &&
      //     filter.required &&
      //     !filter.asAdditional)
      //     ? 'requiredFilter__Border'
      //     : ''
      // }`}
      >
        <Select
          aria-readonly={filter.readonly}
          disabled={filter.readonly}
          onChange={handleChange}
          onBlur={handleBlur}
          value={value}
          className={`filter ${
            filter.error ||
            (shouldShowRedBorderOnRequired !== false &&
              filter.required &&
              !filter.asAdditional)
              ? 'requiredFilter__Border'
              : ''
          }`}
          // className="filter"
          title={filter.title ?? filter.toolTip ?? String(filter.id)}
          ref={assignRefs as ForwardedRef<HTMLSelectElement>}
          aria-label={filter.title ?? filter.toolTip ?? String(filter.id)}
          onClick={handleClick}
          //sx={getFieldErrorStyles(!filter.error)}
          name={filter.id as string}
          data-test-id={`filter-${filter.id}`}
        >
          {options}
        </Select>
      </Box>
    ) : (
      <Input
        aria-readonly={filter.readonly}
        readOnly={filter.readonly}
        value={value}
        type={(filter.type === 'N' ? 'number' : filter.type) ?? 'text'}
        placeholder={filter.placeholder}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={(ev) => {
          if (ev.key.toLowerCase() === 'enter') {
            handleBlur(ev);
            if (onPressEnter)
              onPressEnter((ev.target as HTMLInputElement).value);
          }
        }}
        onClick={handleClick}
        {...window.getVariant('input')}
        className={`filter ${
          filter.error ||
          (shouldShowRedBorderOnRequired !== false &&
            filter.required &&
            !filter.asAdditional &&
            value === '')
            ? 'requiredFilter__Border'
            : ''
        }`}
        title={filter.title ?? filter.toolTip ?? String(filter.id)}
        ref={assignRefs as ForwardedRef<HTMLInputElement>}
        aria-label={filter.title ?? filter.toolTip ?? String(filter.id)}
        name={filter.id as string}
        data-test-id={`filter-${filter.id}`}
      />
    );
  }
  return null;
};
const ApiaFilter = React.memo(React.forwardRef(ApiaFilterRef));
export default ApiaFilter;
